import {jsx} from 'panel';
import {VNode} from 'snabbdom/vnode';

import {CheckboxEl} from 'common/components/checkbox';
import {RadioButtonEl} from 'common/components/radio-button';
import {filterAttributes} from 'common/util';
import {SvgIconEl} from 'common/components/svg-icon';
// eslint-disable-next-line import/no-cycle
import {ItemsMenuEl} from 'common/components/items-menu';
import {
  DropdownPositioning,
  EventHandler,
  HTMLElementHandler,
  ItemAndEventHandler,
  ItemHandler,
  Section,
  SectionItemContextPanelTransform,
  WrappedSection,
  WrappedSectionItem,
} from 'common/components/items-menu/types';

const LabelWithMatches = ({label, labelMatches}) => {
  if (labelMatches) {
    return (
      <span sel='.label'>
        {labelMatches.map((part) => (
          <span sel='.match-part'>{part}</span>
        ))}
      </span>
    );
  }

  return <span sel='.label'>{label}</span>;
};

const UserControlWrapper = (
  {
    isFocused,
    isRadioButton,
    isCheckbox,
    isAnchorLink,
    item,
  }: {
    isRadioButton: boolean;
    isCheckbox: boolean;
    isAnchorLink: boolean;
    isFocused: boolean;
    item: WrappedSectionItem;
  },
  children: VNode | VNode[],
) => {
  if (isCheckbox) {
    return (
      <CheckboxEl
        attrs={filterAttributes({
          elref: `checkbox`,
          checked: item.isSelected,
          /** if color is not provided, will be standard $blue1 */
          'checked-color': item.isSelectAll || item.isSelectAllMatching ? `none` : item.color || null,
          disabled: item.isDisabled,
          'unchecked-icon': item.isSelectAll || item.isSelectAllMatching ? `dash` : ``,
          focused: isFocused,
        })}
      >
        {children}
      </CheckboxEl>
    );
  }

  if (isRadioButton) {
    return (
      <RadioButtonEl
        attrs={filterAttributes({
          selected: item.isSelected,
        })}
      >
        {children}
      </RadioButtonEl>
    );
  }

  if (isAnchorLink) {
    return (
      <a sel='.list-option-link' attrs={{href: item.href, target: item.target}}>
        {children}
      </a>
    );
  }
  return <>{children}</>;
};

const SectionItemIcon = ({
  item,
  alignAllText,
  iconRenderer,
}: {
  item: WrappedSectionItem;
  alignAllText: boolean;
  iconRenderer: WrappedSection['iconRenderer'];
}) => {
  const useCheckmark = item.labelTheme === `inverted-checkmark`;
  const listIcon = item.icon || (useCheckmark && `checkmark`);

  if (typeof iconRenderer === `function`) {
    return <div sel='.custom-icon'>{iconRenderer(item)}</div>;
  }

  return (
    (listIcon || alignAllText) && (
      <div
        sel='.option-icon.item-icon'
        attrs={{icon: listIcon}}
        class={{
          [`align-${item.iconSide || `left`}`]: true,
          'align-top': Boolean(item.subLabel),
        }}
      >
        <SvgIconEl attrs={{icon: listIcon, empty: !listIcon}} />
      </div>
    )
  );
};

const SectionItemLabel = ({item, isRadioButton}: {item: WrappedSectionItem; isRadioButton: boolean}) => (
  <div
    sel='.option-label-section'
    class={{
      'radio-button': isRadioButton,
    }}
  >
    <div
      sel='.option-label'
      attrs={{
        title: item.label,
      }}
      class={{
        [`theme-${item.labelTheme}`]: Boolean(item.labelTheme),
        truncate: !item.wrapLabel,
      }}
    >
      <LabelWithMatches label={item.label} labelMatches={item.labelMatch?.matchParts} />
    </div>
    {item.subLabel && (
      <div
        sel='.option-sub-label'
        attrs={{title: item.subLabel}}
        class={{
          [`theme-${item.subLabelTheme}`]: Boolean(item.subLabelTheme),
          truncate: !item.wrapSubLabel,
        }}
      >
        <LabelWithMatches label={item.subLabel} labelMatches={item.subLabelMatch?.matchParts} />
      </div>
    )}
  </div>
);

const SectionItemAction = ({
  item,
  isFocused,
  isPillFocused,
  actionsRenderer,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  item: WrappedSectionItem;
  isFocused: boolean;
  isPillFocused: boolean;
  actionsRenderer: WrappedSection['actionsRenderer'];
  onClick: ItemAndEventHandler;
  onMouseEnter: ItemHandler;
  onMouseLeave: ItemHandler;
}) => {
  if (typeof actionsRenderer === `function`) {
    return <div sel='.custom-actions'>{actionsRenderer(item)}</div>;
  }

  if (item.hasPill) {
    return (
      <div
        sel='.secondary-action.pill'
        class={{
          focused: isFocused && isPillFocused,
          disabled: item.isPillDisabled,
        }}
        on={{
          click: !item.isPillDisabled ? (ev) => onClick?.(item, ev) : null,
          mouseenter: () => onMouseEnter?.(item),
          mouseleave: () => onMouseLeave?.(item),
        }}
      >
        <span>{item.pillLabel}</span>
        <div sel='.pill-caret'>
          <SvgIconEl attrs={{icon: `filled-caret-right`}} />
        </div>
      </div>
    );
  }

  if ((item.subSections && item.subSections.length) || item.hasCaret) {
    return (
      <div sel='.secondary-action.caret'>
        <SvgIconEl attrs={{icon: `filled-caret-right`}} />
      </div>
    );
  }
  return null;
};

export const SectionItemEl = (
  {
    item,
    isMultiple,
    displayAllSelectOne,
    alignAllText,
    isFocused,
    isPillFocused,
    iconRenderer,
    actionsRenderer,
    onActionClick,
    onActionMouseEnter,
    onActionMouseLeave,
  }: {
    item: WrappedSectionItem;
    isMultiple: boolean;
    displayAllSelectOne: boolean;
    alignAllText: boolean;
    isFocused: boolean;
    isPillFocused: boolean;
    iconRenderer: WrappedSection['iconRenderer'];
    actionsRenderer: WrappedSection['actionsRenderer'];
    onActionClick: ItemAndEventHandler;
    onActionMouseEnter: ItemHandler;
    onActionMouseLeave: ItemHandler;
  },
  sectionItemSubMenuChild: VNode,
) => {
  const isRadioButton = isMultiple && displayAllSelectOne;
  const isCheckbox = isMultiple && !item.isExpander && !item.isControlItem && !displayAllSelectOne;
  const isAnchorLink = Boolean(item.href);

  return (
    <UserControlWrapper
      isRadioButton={isRadioButton}
      isCheckbox={isCheckbox}
      isAnchorLink={isAnchorLink}
      isFocused={isFocused}
      item={item}
    >
      <SectionItemIcon item={item} alignAllText={alignAllText} iconRenderer={iconRenderer} />
      <SectionItemLabel item={item} isRadioButton={isRadioButton} />
      <SectionItemAction
        item={item}
        isFocused={isFocused}
        isPillFocused={isPillFocused}
        onClick={onActionClick}
        onMouseEnter={onActionMouseEnter}
        onMouseLeave={onActionMouseLeave}
        actionsRenderer={actionsRenderer}
      />
      {sectionItemSubMenuChild}
    </UserControlWrapper>
  );
};

export const SectionItemSubMenuEl = ({
  sections,
  dropdownPositioning,
  isMultiple,
  contextPanelTransform,
  keydownProxyEl,
  onNavigateBack,
  onClickItem,
  onFocusItem,
  onInsert,
  onDestroy,
}: {
  sections: Section[];
  dropdownPositioning: DropdownPositioning;
  isMultiple: boolean;
  contextPanelTransform: SectionItemContextPanelTransform;
  keydownProxyEl: HTMLElement;
  onNavigateBack: () => void;
  onClickItem: EventHandler;
  onFocusItem: EventHandler;
  onInsert: HTMLElementHandler;
  onDestroy: () => void;
}) => {
  return (
    <div
      sel='.option-subsections'
      style={dropdownPositioning.styles}
      class={{
        [`is-${dropdownPositioning.horizontal}-aligned`]: true,
        [`is-${dropdownPositioning.vertical}-aligned`]: true,
      }}
      hook={{
        insert: ({elm}) => onInsert?.(elm as HTMLElement),
        destroy: () => onDestroy?.(),
      }}
      on={{
        click: (ev) => ev.stopPropagation(),
      }}
    >
      <ItemsMenuEl
        attrs={{
          elref: `nestedList`,
          multiple: isMultiple,
        }}
        props={{
          sections,
          contextPanelTransform,
          keydownProxyEl: keydownProxyEl || this,
        }}
        on={{
          navigateBack: () => onNavigateBack(),
          clickItem: onClickItem,
          focusItem: onFocusItem,
        }}
      />
    </div>
  );
};
